// We set here the default values of new features
// to safely init our app in case we do smth wrong in firebase

import { isVersionAtLeast, isVersionValid } from './utils/version';

type FeatureFlagValue = {
    web?: string | number | boolean;
    mobile?: string | number | boolean;
};

type FeatureFlags = Record<string, FeatureFlagValue>;

/** Which users the AI feature is offered to, inside a region that allows it at all. */
const AI_FEATURE_AUDIENCES = [
    'allowlist',
    'premium',
    'allowlist_or_premium',
    'all',
] as const;

type AiFeatureAudience = (typeof AI_FEATURE_AUDIENCES)[number];

/**
 * Audience we fall back to when the remote value is missing or unrecognised.
 * Never 'all' - a config mistake must not hand the feature to everybody.
 */
const DEFAULT_AI_FEATURE_AUDIENCE: AiFeatureAudience = 'premium';

/** Audiences a subscription would actually unlock, ie the ones where a paywall makes sense. */
const PAYWALLED_AI_FEATURE_AUDIENCES: readonly AiFeatureAudience[] = [
    'premium',
    'allowlist_or_premium',
] as const;

const featureFlags: FeatureFlags = {
    // EG: test_feature: { web: false, mobile: false },
    // Disable v3 streaming sync and use legacy v2+v1 fallback instead
    // When true, forces fallback mode permanently (no v3 attempts)
    disable_v3_sync: { mobile: true },
    disable_v3_push: { mobile: true },
    ai_feature_enabled_v2: { mobile: false },
    // Minimum app version the AI feature is enabled from; blank means no minimum
    ai_feature_from_version: { mobile: '' },
    // JSON array of user emails granted the AI feature on top of the audience rule.
    // NEVER let the Firebase value become an empty array: bundles released before
    // ai_feature_audience existed read an empty list as "every user", with no region and no
    // subscription check, so emptying it hands the feature out free on every install that has
    // not updated yet. The value carries a dead address as a sentinel to keep it non-empty.
    ai_form_to_entry_users: { mobile: '[]' },
    // JSON array of region codes the AI feature is offered in; empty array means every region
    ai_allowed_regions: { mobile: '["US"]' },
    // One of AI_FEATURE_AUDIENCES; decides who inside an allowed region gets the AI feature
    ai_feature_audience: { mobile: DEFAULT_AI_FEATURE_AUDIENCE },
};

const getFeatureFlagsInitValuesFor = (platform: 'web' | 'mobile') => {
    const keys = Object.keys(featureFlags);
    const defaultValues = keys.reduce(
        (prev, key) => ({ ...prev, [key]: featureFlags[key][platform] }),
        {},
    );
    return defaultValues;
};

// Raw AI remote config values, kept unparsed so getAiFeatureFlags owns the parsing.
type AiRemoteConfig = {
    isAiFeatureEnabled: boolean;
    aiFeatureFromVersion: string;
    aiFormToEntryUsers: string;
    aiAllowedRegions: string;
    aiFeatureAudience: string;
};

/** Default AI remote config, read from the mobile flag defaults so the keys stay next to their definitions. */
function getDefaultAiRemoteConfig(): AiRemoteConfig {
    return {
        isAiFeatureEnabled: Boolean(featureFlags.ai_feature_enabled_v2.mobile),
        aiFeatureFromVersion: String(
            featureFlags.ai_feature_from_version.mobile ?? '',
        ),
        aiFormToEntryUsers: String(
            featureFlags.ai_form_to_entry_users.mobile ?? '',
        ),
        aiAllowedRegions: String(featureFlags.ai_allowed_regions.mobile ?? ''),
        aiFeatureAudience: String(
            featureFlags.ai_feature_audience.mobile ??
                DEFAULT_AI_FEATURE_AUDIENCE,
        ),
    };
}

/** Reads a JSON string-array flag, falling back to `onInvalid` when blank or malformed. */
function parseStringArrayFlag(
    raw: unknown,
    onInvalid: string[] = [],
): string[] {
    if (Array.isArray(raw)) {
        return raw.map(String);
    }

    if (!raw) {
        return onInvalid;
    }

    try {
        const parsed = JSON.parse(String(raw));
        return Array.isArray(parsed) ? parsed.map(String) : onInvalid;
    } catch {
        return onInvalid;
    }
}

/** Reads the audience flag, falling back to the default when blank or unrecognised. */
function parseAiFeatureAudience(raw: unknown): AiFeatureAudience {
    const candidate = String(raw ?? '') as AiFeatureAudience;

    return AI_FEATURE_AUDIENCES.includes(candidate)
        ? candidate
        : DEFAULT_AI_FEATURE_AUDIENCE;
}

/** Case-insensitive region match; an empty allowlist means every region qualifies. */
function isRegionAllowed(
    regionCode: unknown,
    allowedRegions: string[],
): boolean {
    if (allowedRegions.length === 0) {
        return true;
    }

    if (!regionCode) {
        return false;
    }

    const normalisedRegion = String(regionCode).toUpperCase();

    return allowedRegions.some(
        (region) => region.toUpperCase() === normalisedRegion,
    );
}

/** Emails are case-insensitive, so the remote allowlist is matched that way too. */
function isEmailAllowlisted(email: unknown, allowedEmails: string[]): boolean {
    if (!email) {
        return false;
    }

    const normalisedEmail = String(email).trim().toLowerCase();

    return allowedEmails.some(
        (allowed) => allowed.trim().toLowerCase() === normalisedEmail,
    );
}

type AiAudienceMembership = {
    isAllowlisted: boolean;
    hasActiveSubscription: boolean;
};

function isInAiAudience(
    audience: AiFeatureAudience,
    { isAllowlisted, hasActiveSubscription }: AiAudienceMembership,
): boolean {
    switch (audience) {
        case 'allowlist':
            return isAllowlisted;
        case 'premium':
            return hasActiveSubscription;
        case 'allowlist_or_premium':
            return isAllowlisted || hasActiveSubscription;
        case 'all':
            return true;
        default: {
            const exhaustiveCheck: never = audience;
            return exhaustiveCheck;
        }
    }
}

type AiFeatureFlagsInput = {
    /** ai_feature_enabled_v2 */
    isAiFeatureEnabled: boolean;

    /** ai_feature_from_version; blank means no minimum version */
    aiFeatureFromVersion: unknown;

    /** Version of the running app, eg Device.getVersion() */
    currentAppVersion: unknown;

    /** ai_form_to_entry_users, raw or already parsed */
    aiFormToEntryUsers: unknown;

    /** ai_allowed_regions, raw or already parsed; empty means every region */
    aiAllowedRegions: unknown;

    /** ai_feature_audience */
    aiFeatureAudience: unknown;

    currentUserEmail?: string | null;

    /** Region the user is billed in, eg 'US' */
    currentRegionCode?: string | null;

    /** Whether the active organisation's subscription is in an active state */
    hasActiveSubscription?: boolean;
};

type AiFeatureFlags = {
    /** The feature is offered here and this user may use it right now */
    isAiFormToEntryEnabled: boolean;

    /** The feature is offered here but this user has to subscribe first */
    isAiFormToEntryLocked: boolean;
};

const AI_FEATURE_UNAVAILABLE: AiFeatureFlags = {
    isAiFormToEntryEnabled: false,
    isAiFormToEntryLocked: false,
};

/** Turns the raw AI remote config values into the per-feature booleans the app consumes. */
function getAiFeatureFlags({
    isAiFeatureEnabled,
    aiFeatureFromVersion,
    currentAppVersion,
    aiFormToEntryUsers,
    aiAllowedRegions,
    aiFeatureAudience,
    currentUserEmail,
    currentRegionCode,
    hasActiveSubscription,
}: AiFeatureFlagsInput): AiFeatureFlags {
    // No usable minimum version configured means every version qualifies
    const versionAllowed =
        !isVersionValid(aiFeatureFromVersion) ||
        isVersionAtLeast(currentAppVersion, aiFeatureFromVersion);

    // An explicitly empty array means every region - that is the QA escape hatch - but a
    // blank or malformed value must not, or the feature is one console typo away from
    // worldwide. Fall back to the value we ship, the same way the audience flag does.
    const allowedRegions = parseStringArrayFlag(
        aiAllowedRegions,
        parseStringArrayFlag(getDefaultAiRemoteConfig().aiAllowedRegions),
    );

    const regionAllowed = isRegionAllowed(currentRegionCode, allowedRegions);

    // Outside these gates the feature does not exist here - not even as an upsell
    if (!isAiFeatureEnabled || !versionAllowed || !regionAllowed) {
        return AI_FEATURE_UNAVAILABLE;
    }

    const audience = parseAiFeatureAudience(aiFeatureAudience);

    const isEnabled = isInAiAudience(audience, {
        isAllowlisted: isEmailAllowlisted(
            currentUserEmail,
            parseStringArrayFlag(aiFormToEntryUsers),
        ),
        hasActiveSubscription: Boolean(hasActiveSubscription),
    });

    return {
        isAiFormToEntryEnabled: isEnabled,
        // Only offer the paywall where subscribing would actually unlock the feature
        isAiFormToEntryLocked:
            !isEnabled && PAYWALLED_AI_FEATURE_AUDIENCES.includes(audience),
    };
}

export {
    AI_FEATURE_AUDIENCES,
    DEFAULT_AI_FEATURE_AUDIENCE,
    getAiFeatureFlags,
    getDefaultAiRemoteConfig,
    getFeatureFlagsInitValuesFor,
};

export type { AiFeatureAudience, AiRemoteConfig };
